import { create } from 'zustand';

// Default visible columns for the customers table
const DEFAULT_VISIBLE_COLUMNS = [
  'name',
  'address',
  'customerNumber',
  'type',
  'status',
  'phone',
];

interface CustomersState {
  // Page state
  search: string;
  selectedIds: string[];

  // Pagination
  page: number;
  limit: number;

  // Table filters and columns
  columnFilters: Record<string, string>;
  visibleColumns: string[];

  // Table sorting
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
}

interface CustomersActions {
  // Page actions
  setSearch: (search: string) => void;
  setSelectedIds: (ids: string[]) => void;

  // Pagination actions
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;

  // Table filters and columns actions
  setColumnFilter: (column: string, value: string) => void;
  setColumnFilters: (filters: Record<string, string>) => void;
  clearColumnFilters: () => void;
  setVisibleColumns: (columns: string[]) => void;
  toggleColumn: (column: string) => void;

  // Table sorting actions
  setSortColumn: (column: string) => void;
  setSortDirection: (direction: 'asc' | 'desc') => void;
  toggleSort: (column: string) => void;

  // Utility
  resetAll: () => void;
}

type CustomersStore = CustomersState & CustomersActions;

const initialState: CustomersState = {
  search: '',
  selectedIds: [],
  page: 1,
  limit: 100,
  columnFilters: {},
  visibleColumns: DEFAULT_VISIBLE_COLUMNS,
  sortColumn: 'name',
  sortDirection: 'asc',
};

export const useCustomersStore = create<CustomersStore>()((set) => ({
  ...initialState,

  // Page actions
  setSearch: (search) => set({ search, page: 1 }), // Reset to page 1 on search
  setSelectedIds: (selectedIds) => set({ selectedIds }),

  // Pagination actions
  setPage: (page) => set({ page }),
  setLimit: (limit) => set({ limit, page: 1 }), // Reset to page 1 on limit change

  // Table filters and columns actions
  setColumnFilter: (column, value) =>
    set((state) => ({
      columnFilters: { ...state.columnFilters, [column]: value },
    })),
  setColumnFilters: (columnFilters) => set({ columnFilters }),
  clearColumnFilters: () => set({ columnFilters: {} }),
  setVisibleColumns: (visibleColumns) => set({ visibleColumns }),
  toggleColumn: (column) =>
    set((state) => ({
      visibleColumns: state.visibleColumns.includes(column)
        ? state.visibleColumns.filter((c) => c !== column)
        : [...state.visibleColumns, column],
    })),

  // Table sorting actions
  setSortColumn: (sortColumn) => set({ sortColumn }),
  setSortDirection: (sortDirection) => set({ sortDirection }),
  toggleSort: (column) =>
    set((state) => {
      if (state.sortColumn === column) {
        return { sortDirection: state.sortDirection === 'asc' ? 'desc' : 'asc' };
      }
      return { sortColumn: column, sortDirection: 'asc' };
    }),

  // Utility
  resetAll: () => set(initialState),
}));
